import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Plus, Eye, Download, Trash2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatISTDate } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PER_PAGE = 50;

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-amber-50 text-amber-600 border-amber-200',
    finalized: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    dispatched: 'bg-blue-50 text-blue-600 border-blue-200',
    cancelled: 'bg-rose-50 text-rose-600 border-rose-200'
  };
  const labels = {
    draft: 'Draft',
    finalized: 'Finalized',
    dispatched: 'Dispatched',
    cancelled: 'Cancelled'
  };
  return (
    <span className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

// Mobile Card View Component - defined outside to prevent re-renders
const OrderCard = ({ 
  order, 
  isAdmin, 
  userRole,
  selectedOrders, 
  toggleSelectOrder, 
  canEditStatus, 
  handleStatusChange, 
  updatingStatus,
  downloadInvoice 
}) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`order-card-${order.order_number}`}>
    <div className="flex justify-between items-start">
      <div className="flex items-start gap-3">
        {isAdmin && (
          <input
            type="checkbox"
            checked={selectedOrders.has(order.order_number)}
            onChange={() => toggleSelectOrder(order.order_number)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
        )}
        <div>
          <p className="font-semibold text-slate-900">{order.order_number}</p>
          {(userRole === 'admin' || userRole === 'sales') && (
            <p className="text-sm text-slate-600 mt-0.5">{order.customer_name}</p>
          )}
        </div>
      </div>
      {canEditStatus ? (
        <select
          value={order.status}
          onChange={(e) => handleStatusChange(order.order_number, e.target.value)}
          disabled={updatingStatus[order.order_number]}
          className="text-xs font-medium px-2 py-1 rounded-md border-2 focus:outline-none disabled:opacity-50"
          style={{
            borderColor: order.status === 'draft' ? '#FCD34D' : order.status === 'dispatched' ? '#60A5FA' : order.status === 'cancelled' ? '#F87171' : '#34D399',
            backgroundColor: order.status === 'draft' ? '#FFFBEB' : order.status === 'dispatched' ? '#EFF6FF' : order.status === 'cancelled' ? '#FEF2F2' : '#D1FAE5',
            color: order.status === 'draft' ? '#D97706' : order.status === 'dispatched' ? '#2563EB' : order.status === 'cancelled' ? '#DC2626' : '#059669'
          }}
        >
          <option value="draft">Draft</option>
          <option value="finalized">Finalized</option>
          <option value="dispatched">Dispatched</option>
          <option value="cancelled">Cancelled</option>
        </select>
      ) : (
        getStatusBadge(order.status)
      )}
    </div>
    
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <span className="text-slate-500">Items:</span>
        <span className="ml-2 font-medium text-slate-900">{order.items.length}</span>
      </div>
      <div>
        <span className="text-slate-500">Total:</span>
        <span className="ml-2 font-medium text-slate-900">₹{order.grand_total.toFixed(2)}</span>
        {order.gst_applied && (
          <span className="ml-1 text-xs text-emerald-600">(incl. GST)</span>
        )}
      </div>
    </div>
    
    <div className="text-xs text-slate-500">
      {formatISTDate(order.created_at)}
    </div>
    
    <div className="flex gap-2 pt-2">
      <Link
        to={`/orders/${order.order_number}`}
        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-sm font-medium transition-colors"
      >
        <Eye className="w-4 h-4 mr-1.5" />
        View
      </Link>
      {order.status === 'finalized' && (
        <button
          onClick={() => downloadInvoice(order.order_number)}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4 mr-1.5" />
          Invoice
        </button>
      )}
    </div>
  </div>
);

export const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  // Bulk selection state
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders?page=${page}&per_page=${PER_PAGE}`);
      setOrders(response.data.orders);
      setTotalPages(response.data.total_pages);
      setTotalOrders(response.data.total);
      setCurrentPage(response.data.page);
      // Clear selections when page changes
      setSelectedOrders(new Set());
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(currentPage);
  }, [fetchOrders, currentPage]);

  const downloadInvoice = async (orderNumber) => {
    try {
      const response = await axios.get(`${API}/pdf/invoice/${orderNumber}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-50 text-amber-600 border-amber-200',
      finalized: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      dispatched: 'bg-blue-50 text-blue-600 border-blue-200',
      cancelled: 'bg-rose-50 text-rose-600 border-rose-200'
    };
    const labels = {
      draft: 'Draft',
      finalized: 'Finalized',
      dispatched: 'Dispatched',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const handleStatusChange = async (orderNumber, newStatus) => {
    setUpdatingStatus({ ...updatingStatus, [orderNumber]: true });
    
    try {
      await axios.patch(`${API}/orders/${orderNumber}/status?new_status=${newStatus}`);
      toast.success('Order status updated successfully');
      fetchOrders(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    } finally {
      setUpdatingStatus({ ...updatingStatus, [orderNumber]: false });
    }
  };

  const canEditStatus = user?.role === 'admin' || user?.role === 'sales';

  // Bulk selection handlers
  const toggleSelectOrder = (orderNumber) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderNumber)) {
      newSelected.delete(orderNumber);
    } else {
      newSelected.add(orderNumber);
    }
    setSelectedOrders(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(o => o.order_number)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/orders/bulk-delete`, {
        order_numbers: Array.from(selectedOrders)
      });
      toast.success(response.data.message);
      setShowBulkDeleteModal(false);
      setSelectedOrders(new Set());
      fetchOrders(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete orders');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white border border-slate-200 rounded-lg">
        <div className="text-sm text-slate-600">
          Showing {((currentPage - 1) * PER_PAGE) + 1} - {Math.min(currentPage * PER_PAGE, totalOrders)} of {totalOrders} orders
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => goToPage(1)}
                className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm"
              >
                1
              </button>
              {startPage > 2 && <span className="px-2 text-slate-400">...</span>}
            </>
          )}
          
          {pages.map(page => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`px-3 py-1 rounded-md text-sm ${
                page === currentPage
                  ? 'bg-indigo-600 text-white'
                  : 'hover:bg-slate-100'
              }`}
            >
              {page}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 text-slate-400">...</span>}
              <button
                onClick={() => goToPage(totalPages)}
                className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm"
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="orders-title">
              Orders
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              {user?.role === 'customer' ? 'View and manage your orders' : 'Manage all customer orders'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Bulk Delete Button - Admin Only */}
            {isAdmin && selectedOrders.size > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                data-testid="bulk-delete-button"
                className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete Selected ({selectedOrders.size})
              </button>
            )}
            <Link
              to="/orders/new"
              data-testid="create-order-button"
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Order
            </Link>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-sm sm:text-base text-slate-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <p className="text-slate-600">No orders found</p>
            <Link
              to="/orders/new"
              className="mt-4 inline-block text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Create your first order
            </Link>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {/* Mobile Select All - Admin Only */}
              {isAdmin && (
                <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={selectedOrders.size === orders.length && orders.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600">Select all on this page</span>
                </div>
              )}
              {orders.map((order) => (
                <OrderCard 
                  key={order.order_number} 
                  order={order} 
                  isAdmin={isAdmin}
                  userRole={user?.role}
                  selectedOrders={selectedOrders}
                  toggleSelectOrder={toggleSelectOrder}
                  canEditStatus={canEditStatus}
                  handleStatusChange={handleStatusChange}
                  updatingStatus={updatingStatus}
                  downloadInvoice={downloadInvoice}
                />
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="orders-table">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      {isAdmin && (
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedOrders.size === orders.length && orders.length > 0}
                            onChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                      )}
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Order Number</th>
                      {(user?.role === 'admin' || user?.role === 'sales') && (
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                      )}
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Items</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {orders.map((order) => (
                      <tr key={order.order_number} data-testid={`order-row-${order.order_number}`} className="hover:bg-slate-50 transition-colors">
                        {isAdmin && (
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(order.order_number)}
                              onChange={() => toggleSelectOrder(order.order_number)}
                              data-testid={`select-order-${order.order_number}`}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                        )}
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{order.order_number}</td>
                        {(user?.role === 'admin' || user?.role === 'sales') && (
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-slate-600">{order.customer_name}</td>
                        )}
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-slate-600">{order.items.length} items</td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          ₹{order.grand_total.toFixed(2)}
                          {order.gst_applied && (
                            <span className="ml-1 text-xs text-emerald-600">(GST)</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          {canEditStatus ? (
                            <select
                              value={order.status}
                              onChange={(e) => handleStatusChange(order.order_number, e.target.value)}
                              disabled={updatingStatus[order.order_number]}
                              data-testid={`status-dropdown-${order.order_number}`}
                              className="px-2 lg:px-3 py-1 text-xs font-medium rounded-md border-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{
                                borderColor: order.status === 'draft' ? '#FCD34D' : order.status === 'dispatched' ? '#60A5FA' : order.status === 'cancelled' ? '#F87171' : '#34D399',
                                backgroundColor: order.status === 'draft' ? '#FFFBEB' : order.status === 'dispatched' ? '#EFF6FF' : order.status === 'cancelled' ? '#FEF2F2' : '#D1FAE5',
                                color: order.status === 'draft' ? '#D97706' : order.status === 'dispatched' ? '#2563EB' : order.status === 'cancelled' ? '#DC2626' : '#059669'
                              }}
                            >
                              <option value="draft">Draft</option>
                              <option value="finalized">Finalized</option>
                              <option value="dispatched">Dispatched</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          ) : (
                            getStatusBadge(order.status)
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {formatISTDate(order.created_at)}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Link
                              to={`/orders/${order.order_number}`}
                              data-testid={`view-order-${order.order_number}`}
                              className="inline-flex items-center px-2 lg:px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors text-xs lg:text-sm"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Link>
                            {order.status === 'finalized' && (
                              <button
                                onClick={() => downloadInvoice(order.order_number)}
                                data-testid={`download-invoice-${order.order_number}`}
                                className="inline-flex items-center px-2 lg:px-3 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors text-xs lg:text-sm"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                <span className="hidden lg:inline">Invoice</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowBulkDeleteModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-rose-100">
                  <AlertTriangle className="w-6 h-6 text-rose-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Delete Orders</h2>
                <p className="text-slate-600 text-center mb-2 text-sm sm:text-base">
                  Are you sure you want to delete <strong>{selectedOrders.size} order{selectedOrders.size > 1 ? 's' : ''}</strong>?
                </p>
                <p className="text-rose-600 text-center text-sm mb-6">
                  This action cannot be undone.
                </p>
                <div className="max-h-32 overflow-y-auto mb-4 bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-2">Orders to be deleted:</p>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(selectedOrders).map(orderNum => (
                      <span key={orderNum} className="px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-700">
                        {orderNum}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    data-testid="confirm-bulk-delete-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all active:scale-[0.98] text-base order-1 sm:order-2 disabled:opacity-50"
                  >
                    {bulkDeleting ? 'Deleting...' : `Delete ${selectedOrders.size} Order${selectedOrders.size > 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
